import type { catalogTableFr, glossaryFr } from './catalogue.fr';

type DeepStrings<T> = { [K in keyof T]: T[K] extends string ? string : DeepStrings<T[K]> };

/** English texts for catalog governance (table page, sync, glossary). Same keys as `catalogue.fr.ts`. */
export const catalogTableEn: DeepStrings<typeof catalogTableFr> = {
  tabs: {
    overview: 'Overview',
    schema: 'Schema',
    preview: 'Sample data',
    statistics: 'Statistics',
    history: 'History',
    lineage: 'Lineage',
    usage: 'Usage',
  },
  noDescription: 'No description. Add one in the Definition tab, or generate it.',
  overview: {
    qualifiedName: 'Engine name',
    quality: 'Quality',
    status: 'Status',
    properties: 'Engine properties',
    certifiedBy: 'by {{by}}, on {{date}}',
  },
  status: { active: 'Active', dropped: 'Gone from the engine' },
  dropped: {
    title: 'Table gone from the engine',
    description:
      'The Iceberg catalog no longer knows it since {{date}}. Its governance (owner, tags, glossary, certification) is kept.',
  },
  noEngine: {
    title: 'Table without an engine reference',
    description:
      'This table is declared in the catalog, but no engine knows it: no sample data, statistics or history.',
    short: 'None (table declared by hand)',
  },
  engineMissing: {
    title: 'The engine no longer knows this table',
    description: 'The Iceberg catalog cannot find it. Synchronize the catalog to bring the page up to date.',
  },
  governance: {
    owner: 'Owner',
    ownerHelp: 'A user or a group of the tenant, accountable for the table.',
    noOwner: 'No owner',
    groupLabel: 'Group {{name}}',
    principal: { user: 'User', group: 'Group' },
    stewards: 'Stewards',
    stewardsHelp: 'One entry per steward: user:<id> or group:<id>.',
    tags: 'Tags',
    tagsHelp: 'Lower case, digits and “_ . : -”, 64 characters at most; {{max}} tags at most.',
    certification: 'Certification',
    certificationNote: 'Certification note',
    glossary: 'Glossary terms',
    glossaryHelp: 'One term per line, by name. Known terms: {{terms}}.',
    glossaryEmpty: 'The glossary is empty: create terms in Glossary first.',
    classificationHelp: 'Closed list, read by the future engine policies (masking, filters).',
    editTitle: 'Table governance',
    editDescription: 'Owner, stewards, tags, certification and glossary terms.',
  },
  certification: { certified: 'Certified', deprecated: 'Deprecated', none: 'Not certified' },
  classification: {
    none: 'Unclassified',
    public: 'Public',
    internal: 'Internal',
    confidential: 'Confidential',
    sensitive: 'Sensitive',
    pii: 'Personal data',
  },
  columns: {
    description: 'Description',
    classification: 'Classification',
    edit: 'Edit column {{name}}',
    editTitle: 'Column {{name}}',
    editDescription: 'Description, classification and tags of the column.',
  },
  preview: {
    meta_one: '{{count}} row of {{name}}',
    meta_other: '{{count}} rows of {{name}}',
    truncated: 'first {{limit}} rows',
    empty: { title: 'Empty table', description: 'The engine returns no row for this table.' },
    unavailable: {
      title: 'Sample data unavailable',
      description:
        'Sample data goes through the tenant’s Trino data warehouse, which is not configured here.',
    },
  },
  statistics: {
    records: 'Rows',
    files: 'Files',
    size: 'Size',
    deleteFiles: 'Delete files',
    partitionSpec: 'Partitioning',
    unpartitioned: 'Not partitioned',
    sortOrder: 'Sort order',
    unsorted: 'None',
    snapshot: 'Snapshot',
    computedAt: 'Computed on',
    partial: 'Column metrics read from the first {{files}} data files only.',
    columnsTitle: 'Column statistics',
    columns: {
      name: 'Column',
      nulls: 'Nulls',
      distinct: 'Distinct',
      min: 'Min',
      max: 'Max',
      size: 'Size',
    },
    noColumns: {
      title: 'No column statistics',
      description: 'The data files carry no column metrics.',
    },
  },
  history: {
    subtitle_one: '{{count}} snapshot, newest first.',
    subtitle_other: '{{count}} snapshots, newest first.',
    current: 'Current',
    summary: '+{{added}} rows · −{{deleted}} rows · {{total}} rows in all · {{files}} files · {{size}}',
    operations: { append: 'Append', overwrite: 'Overwrite', replace: 'Replace', delete: 'Delete' },
    empty: { title: 'No snapshot', description: 'The table has received no write yet.' },
  },
  lineage: {
    subtitle: 'OpenLineage dataset {{name}} ({{namespace}}).',
    empty: {
      title: 'No known lineage for this table',
      description: 'No run has read or written this dataset while emitting OpenLineage events yet.',
    },
  },
  usage: {
    queries: 'Queries over 7 days',
    users: 'Users over 7 days',
    lastQueried: 'Last query',
    soon: {
      title: 'Usage: coming soon',
      description:
        'Who queries this table, and how often: the measure comes with listening to Trino queries.',
    },
  },
  actions: {
    query: 'Query in the SQL editor',
    queryShort: 'Query in SQL',
    use: 'Use',
    queryLoading: 'Looking for the Trino data warehouse…',
    queryNoWarehouse: 'No Trino data warehouse in this tenant.',
    notebook: 'Open in a notebook',
    snippets: 'Copy a snippet',
    governance: 'Governance',
  },
  snippets: {
    title: 'Read this table',
    description: 'The same name, {{name}}, from each engine of the platform.',
    kinds: {
      trino: 'Trino SQL',
      spark: 'Spark',
      python: 'Python',
      dask: 'Dask',
      duckdb: 'DuckDB',
      jdbc: 'JDBC',
    },
    languages: { sql: 'SQL', python: 'Python', jdbc: 'JDBC' },
    hints: {
      trino: 'In the console SQL editor or any client connected to the tenant’s Trino.',
      spark: 'In a graal Spark run: the graal catalog is set by the platform.',
      python: 'In a Python, Ray or dbt run, or a session: graal module, read as pandas.',
      dask: 'In a Dask run: one partition per data file, read by the workers.',
      duckdb: 'In a Python run or session: the pyiceberg table loaded into DuckDB.',
      jdbc: 'Trino JDBC driver. Trino is not exposed outside the platform: the host comes from the administrator.',
    },
  },
  notebook: {
    title: 'Open in a notebook',
    description: 'Pick a Python session: its cell is prefilled with reading the table, for you to run.',
    sessions: 'Sessions',
    starting: 'starting',
    newSession: 'New session',
  },
  sync: {
    title: 'Synchronization with the Iceberg catalog',
    run: 'Synchronize',
    loading: 'Reading the last synchronization…',
    never: 'Never synchronized.',
    summary:
      'Last synchronization {{when}} ({{trigger}}): {{seen}} tables seen, {{created}} created, {{updated}} updated, {{dropped}} gone.',
    truncated: 'Truncated listing: no table was marked gone.',
    unavailable: 'Unavailable: no Iceberg catalog is configured on this platform.',
    queued: 'A synchronization is already running: yours will follow it.',
    done: 'Catalog synchronized: {{created}} tables added, {{dropped}} gone.',
    failed: 'Synchronization failed: {{detail}}',
    statuses: {
      never: 'Never',
      running: 'Running',
      queued: 'Queued',
      completed: 'Completed',
      failed: 'Failed',
      disabled: 'Unavailable',
    },
    triggers: { manual: 'manual', schedule: 'scheduled', run: 'end of run', event: 'event' },
  },
};

export const glossaryEn: DeepStrings<typeof glossaryFr> = {
  title: 'Glossary',
  subtitle: 'The business vocabulary of the organization, attached to the tables and columns of the catalog.',
  searchPlaceholder: 'Search a term, a synonym…',
  new: 'New term',
  newTitle: 'New term',
  newSubtitle: 'A unique name, a definition, synonyms. Attaching is done from the table.',
  detailSubtitle: 'Business glossary term',
  created: 'Term “{{name}}” created.',
  duplicate: 'A term already has this name.',
  deleted: 'Term “{{name}}” deleted.',
  deleteFailed: 'Deletion failed: {{detail}}',
  delete: 'Delete term',
  confirmDelete: 'Delete the term “{{name}}”? Its links to tables and columns go with it.',
  columns: {
    name: 'Term',
    synonyms: 'Synonyms',
    description: 'Definition',
    owner: 'Owner',
    updated: 'Updated',
  },
  empty: {
    title: 'Empty glossary',
    description: 'Define the business terms (delivery point, consumption…) to attach them to tables.',
  },
  fields: {
    name: 'Name',
    namePlaceholder: 'Delivery point',
    description: 'Definition',
    descriptionHelp: 'What the term means for the business, in one or two sentences.',
    synonyms: 'Synonyms',
    synonymsHelp: 'One synonym per line; search reads them too.',
    owner: 'Owner',
  },
  tabs: { summary: 'Summary', assets: 'Linked assets', definition: 'Definition' },
  assets: {
    title: 'Linked assets',
    description: 'The tables and columns that carry this term, among those you can read.',
    columns: { asset: 'Asset', kind: 'Kind', location: 'Catalog / database', qualifiedName: 'Engine name' },
    kinds: { table: 'Table', field: 'Column' },
    empty: {
      title: 'No linked asset',
      description: 'Attach this term to a table from its page (Governance).',
    },
  },
};
